import HelpIcon from '@ben/icons/question-line';
import InlineTextButton from '@ben/orion/inline-text-button';
import MultiSelect from '@ben/orion/multi-select';
import Tooltip from '@ben/orion/tooltip';
import type { ObjectFieldTemplateProps, RJSFSchema } from '@rjsf/utils';
import { t } from 'i18next';
import { Fragment, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import Accordion from '../../accordion/accordion';
import Icon from '../../remix-icon';
import Typography from '../../typography/typography';

const FIELD_SEARCH_THRESHOLD = 10; // Minimum number of fields required to enable field searching

/**
 * Custom `object` field template to avoid rendering a `fieldset`
 * which can be problematic when it comes to styling
 */
const ObjectFieldTemplate = ({
  title,
  description,
  properties,
  schema,
  uiSchema,
}: ObjectFieldTemplateProps) => {
  const theme = useTheme();
  const showObjectFields = uiSchema?.showObjectFields;
  const [showFields, setShowFields] = useState<string[]>(
    Array.isArray(showObjectFields) ? showObjectFields : []
  );

  const isSearchable =
    uiSchema?.isSearchable && properties.length > FIELD_SEARCH_THRESHOLD;

  useEffect(() => {
    // Update `showFields` if the `showObjectFields` field option changes
    const newShowObjectFields = uiSchema?.showObjectFields;
    if (Array.isArray(newShowObjectFields)) {
      setShowFields(newShowObjectFields);
    }
  }, [uiSchema?.showObjectFields]);

  if (schema.format === 'collapsible') {
    const filteredProperties = properties.filter((property) => {
      if (isSearchable) {
        return showFields.includes(property.name);
      }
      return true;
    });

    const fieldSearchOptions =
      isSearchable && schema.properties
        ? Object.keys(schema.properties).map((key) => {
            const propSchema = (schema.properties as RJSFSchema)[key];
            return {
              value: key,
              label:
                typeof propSchema === 'object' && propSchema !== null
                  ? (propSchema.title ?? key)
                  : key,
            };
          })
        : undefined;

    // Render fields in an Accordion for collapsible sections
    return (
      <Accordion size="sm" chevronPosition="left" defaultValue={['section']}>
        <Accordion.Item value="section" $hasBorder={false}>
          <Accordion.Control>
            <AccordionHeader>
              <AccordionTitle>
                {title}
                {description && (
                  <Tooltip
                    tooltipText={description}
                    noAnchorRole
                    tooltipAnchorStyle={{ display: 'flex' }}
                  >
                    <Icon icon={HelpIcon} />
                  </Tooltip>
                )}
              </AccordionTitle>
              {showFields.length > 0 && (
                <RemoveAllButton
                  label={t('words.removeAll')}
                  onClick={(event) => {
                    setShowFields([]);
                    event.stopPropagation(); // Prevent accordion toggle
                  }}
                  isUnderlined
                />
              )}
            </AccordionHeader>
          </Accordion.Control>
          <StyledAccordionPanel>
            <ObjectField>
              {isSearchable && fieldSearchOptions && (
                <MultiSelect
                  label={uiSchema.searchLabel}
                  data={fieldSearchOptions}
                  placeholder={t('words.search')}
                  comboboxProps={{ withinPortal: false }}
                  value={showFields}
                  onChange={setShowFields}
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  rightSection={<Fragment />} // Hide dropdown icon
                  searchable
                  clearable
                  maxWidth={theme.input.select.width}
                />
              )}
              {filteredProperties.map(({ content }) => content)}
            </ObjectField>
          </StyledAccordionPanel>
        </Accordion.Item>
      </Accordion>
    );
  }

  const isCollapsible =
    schema.properties &&
    typeof schema.properties === 'object' &&
    Object.values(schema.properties).every((propSchema) => {
      return (
        typeof propSchema === 'object' &&
        propSchema !== null &&
        'format' in propSchema &&
        propSchema.format === 'collapsible'
      );
    });

  const schemaWithExtras = schema as RJSFSchema & {
    hasDivider?: boolean;
    headerGap?: number;
    titleVariant?: TypographyVariant;
  };

  return (
    <Wrapper $hasDivider={schemaWithExtras.hasDivider}>
      {(title || description) && (
        <Header $gap={schemaWithExtras.headerGap}>
          {title && (
            <Typography
              variant={schemaWithExtras.titleVariant ?? TypographyVariant.h3}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant={TypographyVariant.bodyMd1}>
              {description}
            </Typography>
          )}
        </Header>
      )}
      {properties.length > 0 && (
        <ObjectField isCollapsible={isCollapsible}>
          {properties.map(({ content }) => content)}
        </ObjectField>
      )}
    </Wrapper>
  );
};

export default ObjectFieldTemplate;

export const Wrapper = styled.div<{ $hasDivider?: boolean }>`
  ${({ theme, $hasDivider }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};

    ${$hasDivider &&
    css`
      border-top: 1px solid ${theme.palette.neutral[60]};
      padding-top: ${theme.spacing(4)};
    `}
  `}
`;

export const Header = styled.div<{ $gap?: number }>`
  ${({ theme, $gap }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing($gap ?? 2)};
  `}
`;

export const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const AccordionTitle = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `}
`;

export const RemoveAllButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    ${theme.typography.bodySm2};
    color: ${theme.palette.neutral[100]};
  `}
`;

export const ObjectField = styled.div`
  ${({
    theme,
    isCollapsible,
  }: {
    theme: ITheme;
    isCollapsible?: boolean;
  }) => css`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    gap: ${theme.spacing(isCollapsible ? 2 : 4)};

    .dividers {
      :not(:last-of-type) {
        border-bottom: 1px dashed ${theme.palette.neutral[60]};
        padding-bottom: ${theme.spacing(2.5)};
        margin-bottom: ${theme.spacing(-1.5)};
      }
    }
  `}
`;

export const StyledAccordionPanel = styled(Accordion.Panel)`
  ${({ theme }) => css`
    .mantine-Accordion {
      &-content {
        padding: ${theme.spacing(3, 0, 1, 0)};
      }
    }
  `}
`;
